import { border } from 'src/components/foundations/tokens';
import { textStyleCss } from 'src/theme/atlas';
import styled from 'styled-components';

const FORMS_TABLE_ACTIONS_COLUMN_WIDTH = '80px';

export const TABLE_WRAPPER_MIN_HEIGHT = 488;

export const TableWrapper = styled.div<{ $minHeight?: number }>`
  margin-top: 0;
  ${(props) =>
    props.$minHeight == null ? '' : `min-height: ${props.$minHeight}px;`}
  border-radius: ${border.radius.lg}px;
  overflow: hidden;
  ${(props) => props.theme.boxShadows.shadowLarge}
  background-color: ${(props) =>
    props.theme.atlas.semantic.color.surface.default};
  position: relative;
  width: 100%;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;

  &[data-sticky-actions-column] {
    min-width: 0;

    table {
      width: 100%;
    }

    th:first-child {
      position: relative;
      z-index: auto;
    }
    td:first-child {
      position: static;
      z-index: auto;
    }
    th:first-child::before {
      display: none;
    }
    th:first-child::after {
      position: absolute;
      content: '';
      height: 26px;
      width: 0;
      border-right: 1px solid
        ${(props) => props.theme.atlas.semantic.color.ui.loading.default};
      right: 0;
      top: 50%;
      transform: translateY(-50%);
    }

    tbody tr {
      cursor: pointer;
    }

    thead tr {
      background-color: ${(props) =>
        props.theme.atlas.semantic.color.surface.variant};
    }
    th {
      background-color: ${(props) =>
        props.theme.atlas.semantic.color.surface.variant};
      color: ${(props) => props.theme.atlas.semantic.color.type.default};
      height: 56px;
      padding: 18px 20px;
      ${({ theme }) => textStyleCss(theme.atlas.typography.semantic.caption)}
      font-weight: 600;
      border-bottom: 1px solid
        ${(props) => props.theme.atlas.semantic.color.action.primary.hover};
    }

    th:last-child {
      position: sticky;
      right: 0;
      z-index: 1001;
      width: ${FORMS_TABLE_ACTIONS_COLUMN_WIDTH};
      min-width: ${FORMS_TABLE_ACTIONS_COLUMN_WIDTH};
      max-width: ${FORMS_TABLE_ACTIONS_COLUMN_WIDTH};
      box-sizing: border-box;
      margin-right: 0;
      padding: 10px 8px;
      text-align: center;
      background-color: ${(props) =>
        props.theme.atlas.semantic.color.surface.variant};

      > div:first-of-type {
        justify-content: center;
      }
    }
    td:last-child {
      position: sticky;
      right: 0;
      z-index: 10;
      width: ${FORMS_TABLE_ACTIONS_COLUMN_WIDTH};
      min-width: ${FORMS_TABLE_ACTIONS_COLUMN_WIDTH};
      max-width: ${FORMS_TABLE_ACTIONS_COLUMN_WIDTH};
      box-sizing: border-box;
      padding: 10px 8px;
      text-align: center;
      vertical-align: middle;
      background-color: ${(props) =>
        props.theme.atlas.semantic.color.surface.default};
    }
    tbody tr:hover td:last-child {
      background-color: ${(props) =>
        props.theme.atlas.semantic.color.surface.subtle};
    }
    tbody tr:last-child td:last-child {
      border-bottom-right-radius: ${border.radius.lg}px;
    }

    &[data-form-management-actions-scroll] {
      th:last-child::before,
      td:last-child::before {
        content: '';
        position: absolute;
        top: 0;
        bottom: 0;
        right: 100%;
        width: 20px;
        background: linear-gradient(to left, rgba(0, 0, 0, 0.3), transparent);
        opacity: 0.2;
        pointer-events: none;
        border-radius: 0;
      }

      td:last-child::before {
        z-index: -1;
      }

      th:last-child::before {
        z-index: 0;
      }

      th:last-child {
        isolation: isolate;
      }

      th:last-child > * {
        position: relative;
        z-index: 1;
      }
    }
  }
`;

export const AllFormsTableWrapper = styled(TableWrapper)`
  && table thead th {
    border-bottom: none !important;
  }
`;

export const TableAndFooterContainer = styled.div`
  display: inline-flex;
  flex-direction: column;
  min-width: 100%;
  width: fit-content;
  max-width: 100%;
`;

export const FormsTableEmptyStateRoot = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  max-width: 400px;
  margin: 0 auto;
  padding: 16px 24px 32px;
`;

export const FormsTableEmptyStateFigure = styled.div`
  width: 240px;
  height: 240px;
  flex-shrink: 0;

  img {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
`;

export const FormsTableEmptyStateTitle = styled.p`
  margin: 24px 0 12px;
  text-align: center;
  ${({ theme }) => textStyleCss(theme.atlas.typography.semantic.headingH3)}
  color: ${(props) => props.theme.atlas.semantic.color.type.default};
`;

export const FormsTableEmptyStateDescription = styled.p`
  margin: 0;
  text-align: center;
  max-width: 360px;
  ${({ theme }) => textStyleCss(theme.atlas.typography.semantic.bodySmall)}
  color: ${(props) => props.theme.atlas.semantic.color.type.muted};
`;

export const TableLoadingOverlay = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${(props) =>
    props.theme.atlas.semantic.color.surface.default}E6;
  z-index: 10;
  border-radius: ${border.radius.lg}px;
  pointer-events: auto;
`;

export const VisuallyHiddenLiveRegion = styled.span`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip-path: inset(50%);
  white-space: nowrap;
  border: 0;
`;

export const FooterSlot = styled.div`
  min-height: 52px;
  margin-top: 16px;
  align-self: stretch;
`;

export const LanguageTagsWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
`;

export const FormViewLink = styled.button`
  display: block;
  width: 60%;
  padding: 0;
  border: none;
  background: none;
  cursor: pointer;
  text-align: center;
  ${({ theme }) => textStyleCss(theme.atlas.typography.semantic.bodySmall)}
  color: ${(props) => props.theme.atlas.semantic.color.action.primary.default};
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }

  &:focus {
    outline: 2px solid
      ${(props) => props.theme.atlas.semantic.color.ui.focusRing};
    outline-offset: 2px;
    border-radius: 4px;
  }
`;

export const FormTitleCell = styled.span`
  ${({ theme }) => textStyleCss(theme.atlas.typography.semantic.bodySmall)}
  color: ${(props) => props.theme.atlas.semantic.color.type.default};
  text-decoration: underline;
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 250px;
`;
